import { defineStore } from 'pinia';
import { useOrgStore } from '@/stores/org';

/**
 * One cache key per layout, not per object.
 *
 * Most objects have a single layout and keep their bare key, exactly as before.
 * A task has one per type, so « À faire » and « Phase » are two entries: asking
 * for one must not serve the other, and saving one must not leave the other
 * behind as a stale copy for the rest of the session.
 */
const cacheKey = (objectType, variant) => (variant ? `${objectType}:${variant}` : objectType);

export const useObjectConfigStore = defineStore('objectConfig', {
    state: () => ({
        configs: {},
        loaded: {},
        loading: {}
    }),

    getters: {
        getConfig:
            state =>
            (objectType, variant = null) =>
                state.configs[cacheKey(objectType, variant)] || null,
        isLoaded:
            state =>
            (objectType, variant = null) =>
                !!state.loaded[cacheKey(objectType, variant)]
    },

    actions: {
        async loadConfig(objectType, variant = null) {
            const key = cacheKey(objectType, variant);

            if (this.loaded[key]) {
                return this.configs[key];
            }
            if (this.loading[key]) {
                // Wait for the in-flight request by polling
                await new Promise(resolve => {
                    const interval = setInterval(() => {
                        if (!this.loading[key]) {
                            clearInterval(interval);
                            resolve();
                        }
                    }, 50);
                });
                return this.configs[key];
            }

            this.loading[key] = true;
            try {
                const orgStore = useOrgStore();
                const api = orgStore.api;
                const { data } = await axios.get(`${api}/object-config/${objectType}`, {
                    params: variant ? { variant } : {}
                });
                // Answered for an organization since left: not this one's layout.
                if (orgStore.api !== api) return null;
                this.configs[key] = data;
                this.loaded[key] = true;
            } catch {
                // Non-critical: allow graceful degradation
            } finally {
                this.loading[key] = false;
            }

            return this.configs[key] || null;
        },

        /**
         * Take an answer already on its way — the list bootstrap's, which carries
         * the object's base layout (no variant). Callers meanwhile wait on `loading`
         * as for any in-flight request; a part left out (null) falls back to the
         * endpoint.
         *
         * @param {string} objectType
         * @param {Promise<Object|null>} pending
         */
        prime(objectType, pending) {
            const key = cacheKey(objectType, null);
            if (this.loaded[key] || this.loading[key]) {
                return;
            }

            this.loading[key] = true;
            pending.then(
                data => {
                    this.loading[key] = false;
                    if (data == null) {
                        return this.loadConfig(objectType);
                    }
                    this.configs[key] = data;
                    this.loaded[key] = true;
                },
                () => {
                    this.loading[key] = false;
                    return this.loadConfig(objectType);
                }
            );
        },

        /**
         * Drops the object's own entry **and every variant of it**.
         *
         * The subtle half: saving « À faire » changes nothing about « Phase »
         * on the server, but a caller that invalidated only the bare key would
         * leave every variant it had already read in memory — including the one
         * whose fields the save had just renamed, since the fields are shared.
         */
        invalidate(objectType) {
            const prefix = `${objectType}:`;

            for (const key of Object.keys(this.configs)) {
                if (key === objectType || key.startsWith(prefix)) {
                    delete this.loaded[key];
                    delete this.configs[key];
                }
            }

            delete this.loaded[objectType];
        },

        invalidateAll() {
            this.configs = {};
            this.loaded = {};
            this.loading = {};
        }
    }
});
